import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Sparkles, Upload, Send, CheckCircle2, User, Phone, Mail, MapPin, Flag, Globe, Trophy, AlertCircle, Loader2 } from 'lucide-react';
import { WINNERS } from '../data';
import { createContestant } from '../firebase/dbSchema';
import { fileToBase64, validateImage } from '../utils/base64Image';
import { sendToPrivyr } from '../utils/sendToPrivyr';
import Seo from '../components/Seo';
import { PAGE_METADATA, getRegisterStructuredData } from '../seo/pageMetadata';

const Register = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    nationality: '',
    origin: '',
    residence: '',
    bio: ''
  });

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        validateImage(file);
        const base64 = await fileToBase64(file);
        setImagePreview(base64);
        setError('');
      } catch (err: any) {
        setError(err.message);
        setImagePreview(null);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!imagePreview) {
      setError('Please upload a photo.');
      return;
    }

    setLoading(true);
    setError('');

    // Client-side email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

     try {
       // Send to Firebase
       await createContestant({
         name: formData.name.trim(),
         email: formData.email.trim().toLowerCase(),
         phone: formData.phone.trim(),
         address: formData.address.trim(),
         nationality: formData.nationality.trim(),
         origin: formData.origin.trim(),
         residence: formData.residence.trim(),
         bio: formData.bio.trim() || `Aspiring Delight Business Empowerment applicant from ${formData.residence}`,
         image: imagePreview
       });

      sendToPrivyr({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: [
          `Address: ${formData.address}`,
          `Nationality: ${formData.nationality}`,
          `State of Origin: ${formData.origin}`,
          `State of Residence: ${formData.residence}`,
          `Bio: ${formData.bio || `Aspiring Delight Business Empowerment applicant from ${formData.residence}`}`,
        ].join('\n'),
        source: 'Website - Register',
      });

      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      if (err.message?.includes('permissions')) {
        setError('Submission failed: Permission denied. Please try again later.');
      } else if (err.message?.includes('quota')) {
        setError('Submission failed: Database quota exceeded. Please try again later.');
      } else {
        setError(`Submission failed: ${err.message || 'Please check your connection and try again.'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (isSubmitted) {
    return (
      <div className="pt-32 pb-24 min-h-screen flex items-center justify-center px-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-xl w-full bg-white p-12 rounded-[3rem] border border-slate-100 shadow-2xl text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-4xl font-display font-black text-royal-dark mb-4">Registration Successful!</h2>
<p className="text-slate-600 text-lg mb-8 font-medium">
             Thank you for applying to become the next Delight Business Empowerment ambassador. Our team will review your application and contact you soon.
           </p>
          <button 
            onClick={() => setIsSubmitted(false)}
            className="bg-royal-purple text-white px-10 py-4 rounded-2xl font-black hover:bg-royal-dark transition-all"
          >
            Back to Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <Seo {...PAGE_METADATA.register} structuredData={getRegisterStructuredData()} />
      <div className="pt-32 pb-24 overflow-hidden">
      {/* Winners Gallery Section */}
      <section className="mb-24">
        <div className="px-6 max-w-7xl mx-auto mb-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end justify-between gap-6"
          >
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-100 px-4 py-2 rounded-full mb-4">
                <Trophy className="w-4 h-4 text-royal-gold" />
                <span className="text-royal-gold text-[10px] font-black uppercase tracking-[0.2em]">Hall of Fame</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-display font-black text-royal-dark tracking-tighter">
                Our <span className="text-royal-purple">Past Winners</span>
              </h2>
              <p className="text-slate-500 font-medium mt-4 text-lg max-w-2xl">
                Join the league of extraordinary women who have redefined beauty through impact and leadership.
              </p>
            </div>
          </motion.div>
        </div>

        <div className="relative">
          <div className="flex gap-6 overflow-x-auto pb-12 px-6 md:px-[calc((100vw-1280px)/2+24px)] scroll-smooth no-scrollbar snap-x">
            {WINNERS.map((winner, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="min-w-[300px] md:min-w-[450px] aspect-[4/5] relative rounded-[3rem] overflow-hidden group snap-center shadow-2xl shadow-black/10 border border-slate-100"
              >
                <img 
                  src={winner.img} 
                  alt={`Portrait of Miss ${winner.name}, ${winner.title} winner in ${winner.year}`}
                  width={1200}
                  height={1500}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-royal-dark/95 via-royal-dark/20 to-transparent flex flex-col justify-end p-8 md:p-12">
                  <div className="translate-y-6 group-hover:translate-y-0 transition-transform duration-500">
                    <div className="flex items-center gap-2 text-royal-gold mb-2">
                      <Sparkles className="w-4 h-4 fill-royal-gold" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Crowned {winner.year}</span>
                    </div>
                    <span className="text-white/60 font-black text-[10px] uppercase tracking-widest block mb-1">{winner.title}</span>
                    <h4 className="text-3xl font-display font-black text-white">Miss {winner.name}</h4>
                    <div className="h-0 group-hover:h-auto overflow-hidden transition-all duration-500">
                      <p className="text-white/70 text-sm font-medium mt-4 leading-relaxed line-clamp-3">
                        {winner.story}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
            {/* Spacer for end of scroll */}
            <div className="min-w-[1px] h-full pr-6" />
          </div>
        </div>
      </section>

      <section className="px-6 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left: Content */}
          <div className="space-y-8">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <div className="inline-flex items-center gap-2 bg-royal-accent border border-amber-200 px-4 py-2 rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-royal-purple" />
                <span className="text-royal-purple text-[10px] font-black uppercase tracking-[0.2em]">Beauty & Impact</span>
              </div>
<h1 className="text-5xl md:text-7xl font-display font-black text-royal-dark tracking-tighter mb-6 leading-[0.95]">
                 Become the Next <br />
                 <span className="text-royal-purple">Delight Business Empowerment</span>
               </h1>
              <p className="text-xl text-slate-600 leading-relaxed font-medium">
                Are you a woman of substance, beauty, and vision? Join the most prestigious empowerment-focused beauty competition and stand a chance to win life-changing prizes and global visibility.
              </p>
            </motion.div>

            <div className="grid gap-6">
              {[
                { title: "Empowerment Grant", desc: "Win up to $5,000 to scale your business or project." },
                { title: "Global Visibility", desc: "Featured on major platforms and international stages." },
                { title: "Mentorship", desc: "Direct access to top industry leaders and business moguls." }
              ].map((item, idx) => (
                <div key={idx} className="flex gap-4 p-6 bg-slate-50 rounded-[2rem] border border-slate-100">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-royal-purple" />
                  </div>
                  <div>
                    <h4 className="font-display font-black text-royal-dark">{item.title}</h4>
                    <p className="text-sm text-slate-500 font-medium">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-8 md:p-12 rounded-[3rem] border border-slate-100 shadow-2xl shadow-black/5"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-2xl font-display font-black text-royal-dark">Registration Form</h3>
                <p className="text-slate-500 text-sm font-medium">Please fill in all details accurately.</p>
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-600 text-sm font-medium">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-royal-dark uppercase tracking-widest flex items-center gap-2">
                    <User className="w-3 h-3 text-royal-purple" /> Full Name
                  </label>
                  <input 
                    required 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-royal-purple/10 focus:border-royal-purple transition-all" 
                    placeholder="Jane Doe" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-royal-dark uppercase tracking-widest flex items-center gap-2">
                    <Phone className="w-3 h-3 text-royal-purple" /> Phone Number
                  </label>
                  <input 
                    required 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-royal-purple/10 focus:border-royal-purple transition-all" 
                    placeholder="+234..." 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-royal-dark uppercase tracking-widest flex items-center gap-2">
                  <Mail className="w-3 h-3 text-royal-purple" /> Email Address
                </label>
                <input 
                  required 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-royal-purple/10 focus:border-royal-purple transition-all" 
                  placeholder="jane@example.com" 
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-royal-dark uppercase tracking-widest flex items-center gap-2">
                  <MapPin className="w-3 h-3 text-royal-purple" /> Residential Address
                </label>
                <input 
                  required 
                  type="text" 
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-royal-purple/10 focus:border-royal-purple transition-all" 
                  placeholder="123 Street Name, City" 
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-royal-dark uppercase tracking-widest flex items-center gap-2">
                    <Globe className="w-3 h-3 text-royal-purple" /> Nationality
                  </label>
                  <input 
                    required 
                    type="text" 
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-royal-purple/10 focus:border-royal-purple transition-all" 
                    placeholder="Nigerian" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-royal-dark uppercase tracking-widest flex items-center gap-2">
                    <Flag className="w-3 h-3 text-royal-purple" /> State of Origin
                  </label>
                  <input 
                    required 
                    type="text" 
                    name="origin"
                    value={formData.origin}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-royal-purple/10 focus:border-royal-purple transition-all" 
                    placeholder="Lagos" 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-royal-dark uppercase tracking-widest flex items-center gap-2">
                  <MapPin className="w-3 h-3 text-royal-purple" /> State of Residence
                </label>
                <input 
                  required 
                  type="text" 
                  name="residence"
                  value={formData.residence}
                  onChange={handleChange}
                  className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-royal-purple/10 focus:border-royal-purple transition-all" 
                  placeholder="Lagos State" 
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-royal-dark uppercase tracking-widest flex items-center gap-2">
                  <Upload className="w-3 h-3 text-royal-purple" /> Upload Full Photo
                </label>
                <div className="relative group">
                  <input 
                    required={!imagePreview}
                    type="file" 
                    accept="image/*" 
                    onChange={handleImageChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" 
                  />
                  <div className="w-full px-8 py-10 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center gap-3 group-hover:border-royal-purple group-hover:bg-royal-accent/20 transition-all">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Contestant registration photo preview"
                        width={128}
                        height={128}
                        className="w-32 h-32 object-cover rounded-lg shadow-md"
                      />
                    ) : (
                      <>
                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                          <Upload className="w-6 h-6 text-slate-400 group-hover:text-royal-purple" />
                        </div>
                        <p className="text-xs font-bold text-slate-500">Click or drag to upload photo</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-royal-dark text-white py-5 rounded-xl font-black text-lg shadow-2xl flex items-center justify-center gap-4 hover:bg-royal-purple transition-all group disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <>
                    Submit Application
                    <Send className="w-6 h-6 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Register;
